"use client";

import type React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  Box,
  Text,
  Code,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useClipboard,
  HStack,
  IconButton,
} from "@chakra-ui/react";
import { CopyIcon, CheckIcon } from "@chakra-ui/icons";

interface DnsRecord {
  name: string;
  type: string;
  value: string;
}

interface DnsRecordsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  dnsRecords: DnsRecord[];
  domain: string;
  onVerify?: () => void;
  isVerifying?: boolean;
}

const DnsRecordsDialog: React.FC<DnsRecordsDialogProps> = ({
  isOpen,
  onClose,
  dnsRecords,
  domain,
  onVerify,
  isVerifying = false,
}) => {
  const isCnameDialog = dnsRecords.some((record) => record.type === "CNAME");

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent maxW="900px">
        <ModalHeader>
          {isCnameDialog
            ? `CNAME Records Required for ${domain}`
            : `DNS Validation Required for ${domain}`}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Alert
              status={isCnameDialog ? "success" : "info"}
              borderRadius="md"
            >
              <AlertIcon />
              <Box flex="1">
                <AlertTitle fontSize="md">
                  {isCnameDialog
                    ? "Deployment Complete!"
                    : "Certificate Requested"}
                </AlertTitle>
                <AlertDescription fontSize="sm">
                  {isCnameDialog
                    ? "Your application has been deployed successfully. Add the following CNAME records to point your domains to the load balancers."
                    : "An SSL certificate has been requested for your domain. To complete the verification, add the following DNS records to your domain's DNS settings."}
                </AlertDescription>
              </Box>
            </Alert>

            <Box>
              <Text fontWeight="bold" mb={2}>
                {isCnameDialog
                  ? "Add these CNAME records:"
                  : "Add these DNS records:"}
              </Text>
              <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th>Type</Th>
                      <Th>Name</Th>
                      <Th>Value</Th>
                      <Th width="50px"></Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {dnsRecords.map((record, index) => (
                      <DnsRecordRow
                        key={index}
                        record={record}
                        domain={domain}
                        isCnameRecord={record.type === "CNAME"}
                      />
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </Box>

            <Alert
              status={isCnameDialog ? "info" : "warning"}
              borderRadius="md"
            >
              <AlertIcon />
              <Box flex="1">
                <AlertDescription fontSize="sm">
                  {isCnameDialog
                    ? "After adding these CNAME records, DNS propagation can take a few minutes to a few hours. Your applications will be accessible once the DNS records propagate."
                    : "DNS propagation can take up to 24-48 hours. The system will automatically check for verification when you return to this page. You can continue with the rest of the setup, but final completion requires verified domains."}
                </AlertDescription>
              </Box>
            </Alert>
          </VStack>
        </ModalBody>

        <ModalFooter>
          {isCnameDialog && onVerify ? (
            <>
              <Button variant="ghost" mr={3} onClick={onClose}>
                I'll do this later
              </Button>
              <Button
                colorScheme="blue"
                onClick={onVerify}
                isLoading={isVerifying}
                loadingText="Setting up..."
              >
                I've added the DNS records
              </Button>
            </>
          ) : (
            <Button colorScheme="blue" onClick={onClose}>
              Got it
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const DnsRecordRow: React.FC<{
  record: DnsRecord;
  domain: string;
  isCnameRecord?: boolean;
}> = ({ record, domain, isCnameRecord = false }) => {
  const getDisplayName = (fullName: string, baseDomain: string): string => {
    if (isCnameRecord) {
      return fullName;
    }

    // Remove trailing dot if present
    const normalizedName = fullName.endsWith(".")
      ? fullName.slice(0, -1)
      : fullName;
    const normalizedDomain = baseDomain.endsWith(".")
      ? baseDomain.slice(0, -1)
      : baseDomain;

    // Extract the root domain (last two parts: domain.com)
    const domainParts = normalizedDomain.split(".");
    const rootDomain = domainParts.slice(-2).join("."); // e.g., "callsupplai.com"

    // If the record name ends with the root domain, remove it and show just the subdomain parts
    if (normalizedName.endsWith(rootDomain)) {
      // Remove ".rootDomain" from the end
      const withoutRootDomain = normalizedName.slice(
        0,
        -(rootDomain.length + 1),
      );
      return withoutRootDomain;
    }

    // Fallback: return as is
    return normalizedName;
  };

  const displayName = getDisplayName(record.name, domain);

  const { hasCopied: copiedName, onCopy: onCopyName } =
    useClipboard(displayName);
  const { hasCopied: copiedValue, onCopy: onCopyValue } = useClipboard(
    record.value,
  );

  return (
    <Tr>
      <Td>
        <Code fontSize="xs">{record.type}</Code>
      </Td>
      <Td>
        <HStack spacing={2}>
          <Code fontSize="xs" wordBreak="break-all" flex="1">
            {displayName}
          </Code>
          <IconButton
            aria-label="Copy name"
            icon={copiedName ? <CheckIcon /> : <CopyIcon />}
            size="xs"
            onClick={onCopyName}
            colorScheme={copiedName ? "green" : "gray"}
          />
        </HStack>
      </Td>
      <Td>
        <HStack spacing={2}>
          <Code fontSize="xs" wordBreak="break-all" flex="1">
            {record.value}
          </Code>
          <IconButton
            aria-label="Copy value"
            icon={copiedValue ? <CheckIcon /> : <CopyIcon />}
            size="xs"
            onClick={onCopyValue}
            colorScheme={copiedValue ? "green" : "gray"}
          />
        </HStack>
      </Td>
    </Tr>
  );
};

export default DnsRecordsDialog;
